// @flow
import * as React from 'react';
import { Box } from 'grommet';
import {
    defineMessages, injectIntl, type IntlShape, FormattedMessage,
} from 'react-intl';
import { connect } from 'react-redux';
import { type WebSocketClient, shallowEqual } from 'speed-js-core';
import type { ApiClient, Confirmation } from 'speed-js-react';
import {
    connectWS, connectAPI, getUser as getUserSelector, withConfirmation,
} from 'speed-js-react';
import EditedByIndicator from 'esm-web-pro/components/maps/EditedByIndicator';
import MapUser from 'esm-web-pro/components/maps/MapUser';
import WSViewerIcon from 'esm-web-pro/components/icons/WSViewerIcon';
import type { Map } from './types';


export const MAP_WS_MESSAGE = 'gis-map';


const messages = defineMessages({
    'map-viewers-title': {
        id: 'map-viewers-title',
        defaultMessage: 'Viewers',
    },
    'map-editor-title': {
        id: 'map-editor-title',
        defaultMessage: 'Editor',
    },
    'map-user-viewing': {
        id: 'map-user-viewing',
        defaultMessage: 'viewing',
    },
    'map-user-editing': {
        id: 'map-user-editing',
        defaultMessage: 'editing',
    },
});

type EditedBy = {
    id: string,
    active: boolean,
} | null;


type Props = {
    map: Map | null,
    ws: WebSocketClient,
    getUser: (userId: string) => Promise<Object>,
    currentUser: Object | null,
    intl: IntlShape,
    onEditFromOtherUser: (EditedBy) => void,
    getLock: (identity: string) => Promise<Object>,
    confirm: (confirmation: Confirmation) => void,
    onMapLocked?: () => void,
    sendOpenedBy: boolean,
    lockActive?: boolean,
};

type State = {
    openedBy: Array<string>,
    editedBy: EditedBy,
}

@connect((state: Object) => ({
    currentUser: getUserSelector(state),
}))
@connectAPI((apiClient: ApiClient) => ({
    getUser: {
        send: (userId: string) => apiClient.resources.user.get(userId),
    },
    getLock: {
        send: (identity: string) => apiClient.resources['networks.map'].getLock(identity),
    },
}))
@connectWS
@injectIntl
@withConfirmation
class MapViewers extends React.PureComponent<Props, State> {
    static defaultProps = {
        getUser: () => Promise.resolve({}),
        getLock: () => Promise.resolve({}),
        ws: {},
        currentUser: null,
        intl: {},
        onEditFromOtherUser: null,
        confirm: () => undefined,
        sendOpenedBy: true,
        onMapLocked: undefined,
        lockActive: false,
    };

    mounted: boolean = true;

    usersMap: { [string]: Object } = {};

    notifyOpenedInterval: IntervalID | null = null;

    notifyEditInterval: IntervalID | null = null;

    state = {
        openedBy: [],
        editedBy: null,
    };

    sendCloseMessage = true;

    componentDidMount(): void {
        this.mounted = true;

        // eslint-disable-next-line
        const { ws, getLock } = this.props;

        ws.onSubscribe(MAP_WS_MESSAGE, () => {
            const { map } = this.props;

            if (this.mounted && map) {
                getLock(map.identity).then(this.handleGetLock);
            }
        });
        ws.subscribe({
            [MAP_WS_MESSAGE]: (data: Object) => {
                const { map } = this.props;

                if (this.mounted && map && data.event === 'open' && map.identity === data.map) {
                    this.setState((prevState) => {
                        const { currentUser } = this.props;

                        let openedBy = [];
                        if (data.opened_by) {
                            openedBy = Object.keys(data.opened_by).filter((openedById) => map && (
                                map.is_draft || data.opened_by[openedById].length > 1
                                || !currentUser || openedById !== currentUser.uuid
                            ));
                        }

                        let editedBy = null;
                        if (
                            data.edited_by
                            && (!map.is_draft || !currentUser || data.edited_by.user_id !== currentUser.uuid)
                        ) {
                            editedBy = {
                                id: data.edited_by.user_id,
                                active: !!data.edited_by.active_connection,
                            };
                        }
                        if (!shallowEqual(prevState.openedBy, openedBy)) {
                            return { openedBy };
                        }
                        if (!shallowEqual(prevState.editedBy, editedBy)) {
                            return { editedBy };
                        }
                        return null;
                    }, this.updateUsersMap);
                }
            },
        });

        window.addEventListener('beforeunload', this.handleBeforeUnload);
    }

    componentDidUpdate(prevProps: Props, prevState: State): void {
        const {
            map, onEditFromOtherUser, getLock,
        } = this.props;
        const { editedBy } = this.state;

        if (
            prevProps.map !== map && (
                (!map || !prevProps.map)
                || (map.uuid !== prevProps.map.uuid || map.is_draft !== prevProps.map.is_draft)
            )
        ) {
            const { ws } = this.props;

            if (prevProps.map) {
                if (this.notifyOpenedInterval !== null) {
                    clearInterval(this.notifyOpenedInterval);
                    this.notifyOpenedInterval = null;
                }
                if (this.notifyEditInterval !== null) {
                    clearInterval(this.notifyEditInterval);
                    this.notifyEditInterval = null;
                }
                if (this.sendCloseMessage) {
                    const action = prevProps.map.is_draft ? 'close_draft' : 'close_map';
                    ws.send(MAP_WS_MESSAGE, {
                        action,
                        identity: prevProps.map.identity,
                    });
                } else {
                    this.sendCloseMessage = true;
                }
            }

            if (map) {
                getLock(map.identity).then(this.handleGetLock);
            }
        }

        if (!shallowEqual(prevState.editedBy, editedBy) && onEditFromOtherUser) {
            if (editedBy) {
                onEditFromOtherUser(editedBy);
            } else {
                onEditFromOtherUser(null);
            }
        }
    }

    componentWillUnmount() {
        this.mounted = false;

        const { ws, map } = this.props;
        if (map) {
            const action = map.is_draft ? 'close_draft' : 'close_map';
            ws.send(MAP_WS_MESSAGE, {
                action,
                identity: map.identity,
            });
        }
        ws.unSubscribe([MAP_WS_MESSAGE]);

        if (this.notifyOpenedInterval !== null) {
            clearInterval(this.notifyOpenedInterval);
            this.notifyOpenedInterval = null;
        }
        if (this.notifyEditInterval !== null) {
            clearInterval(this.notifyEditInterval);
            this.notifyEditInterval = null;
        }

        window.removeEventListener('beforeunload', this.handleBeforeUnload);
    }

    updateUsersMap = () => {
        const { openedBy, editedBy } = this.state;
        const { getUser } = this.props;

        const setUser = (userId) => {
            if (typeof this.usersMap[userId] === 'undefined') {
                getUser(userId).then((result: Object) => {
                    if (!this.mounted) {
                        return;
                    }

                    this.usersMap[userId] = result;
                    this.forceUpdate();
                });
            }
        };

        openedBy.forEach(setUser);
        if (editedBy) {
            setUser(editedBy.id);
        }
    };

    handleBeforeUnload = () => {
        const { ws, map } = this.props;

        if (map) {
            ws.send(MAP_WS_MESSAGE, {
                action: map.is_draft ? 'close_draft' : 'close_map',
                identity: map.identity,
            });
        }
    };

    handleGetLock = (data: Object) => {
        const {
            onEditFromOtherUser, map, currentUser, ws, sendOpenedBy, lockActive,
        } = this.props;

        let openedBy = [];
        if (data.opened_by) {
            openedBy = Object.keys(data.opened_by).filter((openedById) => map && (
                map.is_draft || data.opened_by[openedById].length > 1
                || !currentUser || openedById !== currentUser.uuid
            ));
        }

        let editedBy = null;
        if (
            data.edited_by
            && map && (!map.is_draft || !currentUser || data.edited_by.user_id !== currentUser.uuid)
        ) {
            editedBy = {
                id: data.edited_by.user_id,
                active: !!data.edited_by.active_connection,
            };
        }

        this.setState({ openedBy, editedBy }, this.updateUsersMap);

        if (
            currentUser && data.edited_by && map && map.is_draft && ((
                data.edited_by.active_connection
                && data.edited_by.active_connection !== ws.connectionId
            ) || (
                currentUser.uuid !== data.edited_by.user_id
            ))
        ) {
            const { confirm, onMapLocked } = this.props;

            let message;

            if (data.edited_by.user_id === currentUser.uuid) {
                message = (
                    <FormattedMessage
                        id="editing-in-another-tab"
                        defaultMessage="You are editing this map in another tab or browser!"
                    />
                );
            } else {
                message = (
                    <FormattedMessage
                        id="another-user-editing-map"
                        defaultMessage="Another user is editing this map!"
                    />
                );
            }

            confirm({
                message,
                actions: {
                    close: {
                        label: <FormattedMessage
                            id="close"
                            defaultMessage="Close"
                        />,
                        callback: () => {
                            this.sendCloseMessage = false;
                            if (onMapLocked) {
                                onMapLocked();
                            }
                        },
                    },
                },
            });
            return;
        }

        if (editedBy) {
            onEditFromOtherUser(editedBy);
        } else {
            onEditFromOtherUser(null);
        }

        if (map) {
            if (map.is_draft) {
                ws.send(MAP_WS_MESSAGE, {
                    action: 'open_draft',
                    identity: map.identity,
                    lock_active: lockActive,
                });
                if (this.notifyEditInterval !== null) {
                    clearInterval(this.notifyEditInterval);
                    this.notifyEditInterval = null;
                }
                this.notifyEditInterval = setInterval(() => {
                    ws.send(MAP_WS_MESSAGE, {
                        action: 'open_draft',
                        identity: map.identity,
                        lock_active: lockActive,
                    });
                }, 8000);
            } else if (sendOpenedBy) {
                ws.send(MAP_WS_MESSAGE, {
                    action: 'open_map',
                    identity: map.identity,
                });
                if (this.notifyOpenedInterval !== null) {
                    clearInterval(this.notifyOpenedInterval);
                    this.notifyOpenedInterval = null;
                }
                this.notifyOpenedInterval = setInterval(() => {
                    ws.send(MAP_WS_MESSAGE, {
                        action: 'open_map',
                        identity: map.identity,
                    });
                }, 8000);
            }
        }
    };

    render() {
        const { openedBy, editedBy } = this.state;
        if (!openedBy.length && !editedBy) {
            return null;
        }

        const { intl } = this.props;

        const editedByUser = editedBy ? this.usersMap[editedBy.id] : null;

        return (
            <Box direction="row" pad="xsmall" alignSelf="center">
                {
                    openedBy.length ? (
                        <Box align="center" direction="row">
                            <Box title={intl.formatMessage(messages['map-viewers-title'])}>
                                <WSViewerIcon />
                            </Box>
                            {openedBy.map((userId) => (
                                <MapUser
                                    key={userId}
                                    user={this.usersMap[userId]}
                                    action={intl.formatMessage(messages['map-user-viewing'])}
                                />
                            ))}
                        </Box>
                    ) : null
                }
                {editedByUser && (
                    <EditedByIndicator
                        title={intl.formatMessage(messages['map-editor-title'])}
                        editedBy={editedBy}
                        user={editedByUser}
                        action={intl.formatMessage(messages['map-user-editing'])}
                    />
                )}
            </Box>
        );
    }
}

export default MapViewers;
